import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { userPreferencesApi } from '@/api/userPreferencesApi'
import {
  BOARD_PREFS_KEY,
  DEFAULT_BOARD_PREFS,
  coerceBoardPrefs,
  isDefaultBoardPrefs,
  type BoardElement,
  type BoardPrefs,
} from '@/lib/board/boardPrefs'
import { useAuth } from '@/providers/AuthProvider'
import { TABLE_PREFS_QUERY_KEY } from '@/providers/TableColumnPrefsProvider'

const WRITE_DEBOUNCE_MS = 600

/**
 * The signed-in user's board settings.
 *
 * Reads the same `GET /preferences` map the table layouts use, so opening the
 * board costs no extra request. Writes follow `TableColumnPrefsProvider`:
 * applied at once, saved after a short quiet period, flushed with `keepalive`
 * when the page goes away, and not rolled back on failure (a layout is not data
 * integrity). Defaults are deleted rather than stored.
 */
export function useBoardPrefs() {
  const { isAuthenticated } = useAuth()
  const queryClient = useQueryClient()
  const { data: serverMap } = useQuery({
    queryKey: TABLE_PREFS_QUERY_KEY,
    queryFn: userPreferencesApi.getAll,
    enabled: isAuthenticated,
    staleTime: Infinity,
    retry: 1,
  })

  const [local, setLocal] = useState<BoardPrefs | null>(null)
  const prefs = useMemo(
    () => local ?? (serverMap ? coerceBoardPrefs(serverMap[BOARD_PREFS_KEY]) : DEFAULT_BOARD_PREFS),
    [local, serverMap],
  )

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pending = useRef<BoardPrefs | null>(null)

  const flush = useCallback(
    (unloading = false) => {
      if (timer.current) {
        clearTimeout(timer.current)
        timer.current = null
      }
      const next = pending.current
      if (!next) return
      pending.current = null
      const opts = unloading ? { keepalive: true } : undefined
      const keep = !isDefaultBoardPrefs(next)
      const save = keep
        ? userPreferencesApi.set(BOARD_PREFS_KEY, next, opts)
        : userPreferencesApi.remove(BOARD_PREFS_KEY, opts)
      void save
        .then(() => {
          queryClient.setQueryData<Record<string, unknown>>(TABLE_PREFS_QUERY_KEY, prev => {
            const map = { ...(prev ?? {}) }
            if (keep) map[BOARD_PREFS_KEY] = next
            else delete map[BOARD_PREFS_KEY]
            return map
          })
        })
        .catch(() => {})
    },
    [queryClient],
  )

  const update = useCallback(
    (next: BoardPrefs) => {
      setLocal(next)
      pending.current = next
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => flush(), WRITE_DEBOUNCE_MS)
    },
    [flush],
  )

  useEffect(() => {
    const onPageHide = () => flush(true)
    const onHidden = () => {
      if (document.visibilityState === 'hidden') flush(true)
    }
    window.addEventListener('pagehide', onPageHide)
    document.addEventListener('visibilitychange', onHidden)
    return () => {
      window.removeEventListener('pagehide', onPageHide)
      document.removeEventListener('visibilitychange', onHidden)
      flush(false)
    }
  }, [flush])

  return {
    prefs,
    setColumns: (columns: number) => update(coerceBoardPrefs({ ...prefs, columns })),
    setShown: (key: BoardElement, shown: boolean) =>
      update(coerceBoardPrefs({ ...prefs, show: { ...prefs.show, [key]: shown } })),
    reset: () => update(DEFAULT_BOARD_PREFS),
  }
}
